import React from 'react';

/**
 * Reusable daily bar chart for time-series analytics.
 *
 * Props:
 *   - data: array of { date: string (ISO/YYYY-MM-DD), value: number }
 *   - title: chart header (defaults to "Daily Counts")
 *   - formatValue: (number) => string used for both bar labels and y-axis ticks
 *
 * Extracted from the inline `EventChart` previously living in
 * `ClinicAnalyticsSection.jsx` so it can be reused by TokenUsageSection
 * and other analytics surfaces.
 */
const DailyBarChart = ({ data = [], title = 'Daily Counts', formatValue }) => {
  const fmt = formatValue || ((n) => Number(n).toLocaleString());

  const width = 800;
  const height = 300;
  const padding = 40;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  const safeData = Array.isArray(data) ? data : [];

  if (safeData.length === 0) {
    return (
      <div className="chart-container bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">{title}</h3>
        <div className="text-sm text-gray-500">No data available for the selected period.</div>
      </div>
    );
  }

  const maxValue = Math.max(...safeData.map((item) => item.value || 0), 0);
  const safeMax = maxValue === 0 ? 1 : maxValue;

  const bars = safeData.map((item, index) => {
    const barWidth = chartWidth / safeData.length - 4;
    const barHeight = ((item.value || 0) / safeMax) * chartHeight;
    const x = padding + index * (chartWidth / safeData.length);
    const y = height - padding - barHeight;

    return (
      <g key={item.date}>
        <rect x={x} y={y} width={barWidth} height={barHeight} fill="#4a90e2" />
        <text
          x={x + barWidth / 2}
          y={height - padding + 20}
          textAnchor="middle"
          fontSize="10"
          fill="#555"
        >
          {new Date(item.date).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
          })}
        </text>
        <text
          x={x + barWidth / 2}
          y={y - 5}
          textAnchor="middle"
          fontSize="10"
          fill="#555"
        >
          {fmt(item.value || 0)}
        </text>
      </g>
    );
  });

  const yAxisLabels = [];
  const numLabels = 5;
  for (let i = 0; i <= numLabels; i++) {
    const value = (maxValue / numLabels) * i;
    const y = height - padding - (i / numLabels) * chartHeight;

    yAxisLabels.push(
      <g key={`y-${i}`}>
        <line
          x1={padding - 5}
          y1={y}
          x2={width - padding}
          y2={y}
          stroke="#ddd"
          strokeWidth="1"
        />
        <text
          x={padding - 10}
          y={y + 5}
          textAnchor="end"
          fontSize="10"
          fill="#555"
        >
          {fmt(Math.round(value))}
        </text>
      </g>,
    );
  }

  return (
    <div className="chart-container bg-white p-6 rounded-lg shadow-md">
      <h3 className="text-lg font-semibold text-gray-700 mb-4">{title}</h3>
      <svg width={width} height={height} className="w-full max-w-4xl">
        {yAxisLabels}
        {bars}
        <line
          x1={padding}
          y1={height - padding}
          x2={width - padding}
          y2={height - padding}
          stroke="#555"
          strokeWidth="1"
        />
        <line
          x1={padding}
          y1={padding}
          x2={padding}
          y2={height - padding}
          stroke="#555"
          strokeWidth="1"
        />
      </svg>
    </div>
  );
};

export default DailyBarChart;
